import { createTheme } from '@mui/material/styles';

// Warm & Inviting color palette
const warmColors = {
  primary: {
    main: '#D97706',      // Deep amber
    light: '#F59E0B',     // Lighter amber
    dark: '#B45309',      // Darker amber
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#F97316',      // Warm coral/orange
    light: '#FB923C',
    dark: '#EA580C',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#FEFBF6',   // Cream background
    paper: '#FFFBF5',     // Warm white for cards
    warm: '#FFF8F0',      // Warm tint for sticky elements
  },
  text: {
    primary: '#2D2A26',   // Warm dark gray
    secondary: '#6B6560', // Warm medium gray
  },
  success: {
    main: '#16A34A',      // Green for "Open Now"
    light: '#22C55E',
    dark: '#15803D',
  },
  divider: 'rgba(45, 42, 38, 0.12)',
};

const theme = createTheme({
  palette: {
    ...warmColors,
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 10,
  },
  spacing: 8, // Base spacing unit
  shadows: [
    'none',
    '0px 2px 4px rgba(45, 42, 38, 0.05)',
    '0px 4px 8px rgba(45, 42, 38, 0.08)',
    '0px 6px 12px rgba(45, 42, 38, 0.1)',
    '0px 8px 16px rgba(45, 42, 38, 0.12)',
    '0px 10px 20px rgba(45, 42, 38, 0.14)',
    '0px 12px 24px rgba(45, 42, 38, 0.16)',
    '0px 14px 28px rgba(45, 42, 38, 0.18)',
    '0px 16px 32px rgba(45, 42, 38, 0.2)',
    // Fill remaining shadow slots with warm tones
    '0px 18px 36px rgba(45, 42, 38, 0.22)',
    '0px 20px 40px rgba(45, 42, 38, 0.24)',
    '0px 22px 44px rgba(45, 42, 38, 0.26)',
    '0px 24px 48px rgba(45, 42, 38, 0.28)',
    '0px 26px 52px rgba(45, 42, 38, 0.30)',
    '0px 28px 56px rgba(45, 42, 38, 0.32)',
    '0px 30px 60px rgba(45, 42, 38, 0.34)',
    '0px 32px 64px rgba(45, 42, 38, 0.36)',
    '0px 34px 68px rgba(45, 42, 38, 0.38)',
    '0px 36px 72px rgba(45, 42, 38, 0.40)',
    '0px 38px 76px rgba(45, 42, 38, 0.42)',
    '0px 40px 80px rgba(45, 42, 38, 0.44)',
    '0px 42px 84px rgba(45, 42, 38, 0.46)',
    '0px 44px 88px rgba(45, 42, 38, 0.48)',
    '0px 46px 92px rgba(45, 42, 38, 0.50)',
    '0px 48px 96px rgba(45, 42, 38, 0.52)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: '0px 4px 8px rgba(217, 119, 6, 0.25)',
          '&:hover': {
            boxShadow: '0px 6px 12px rgba(217, 119, 6, 0.35)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: warmColors.background.paper,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: warmColors.background.paper,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
        filled: {
          backgroundColor: warmColors.primary.main,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: warmColors.primary.dark,
          },
        },
        outlined: {
          borderColor: warmColors.primary.main,
          color: warmColors.primary.main,
          '&:hover': {
            backgroundColor: 'rgba(217, 119, 6, 0.08)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: warmColors.background.warm,
          color: warmColors.text.primary,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: warmColors.background.warm,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 20,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: warmColors.primary.main,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: warmColors.primary.main,
            '& + .MuiSwitch-track': {
              backgroundColor: warmColors.primary.light,
            },
          },
        },
      },
    },
  },
});

export default theme;
